import { useEffect, useRef, useState } from 'react';
import { useBulkImportJobStore } from '@shared/store/bulkImportJobStore';
import { getImportStatus } from '../services/bulkImportService';

const POLL_INTERVAL_MS = 3000;

/** Ceiling on the 429 backoff, so a long cool-off still recovers within one poll of the window. */
const MAX_BACKOFF_MS = 60000;

/**
 * Wait before the next poll after a 429. The server sends Retry-After on a rate-limit rejection, so
 * honour it when present; otherwise double the interval each time rather than re-hammering an
 * exhausted window and keeping it exhausted.
 *
 * @param {Object} error Axios error for the rejected request.
 * @param {number} previousDelay Delay used for the attempt that was rejected.
 * @returns {number} Milliseconds to wait.
 */
function backoffAfterRateLimit(error, previousDelay) {
  const retryAfter = Number(error?.response?.headers?.['retry-after']);

  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter * 1000, MAX_BACKOFF_MS);
  }

  return Math.min(previousDelay * 2, MAX_BACKOFF_MS);
}

/**
 * Polls an import's progress until it reaches a terminal state.
 *
 * The server decides what "terminal" means (`isTerminal`) rather than the client matching on status
 * strings, so a new status can be added server-side without the SPA polling forever.
 *
 * Only one caller should run this for a given job: the endpoint is rate limited, and a second
 * poller would halve the window each of them gets. That owner is BulkImportStatusIndicator, which
 * is mounted for the whole authenticated app; everything else reads the result from
 * bulkImportJobStore.
 *
 * @param {number|null} jobId Job to watch, or null to stay idle.
 * @returns {{ status: Object|null, isPolling: boolean }}
 */
export function useBulkImportStatus(jobId) {
  const setStoreStatus = useBulkImportJobStore((state) => state.setStatus);
  // Stored with the job it describes. Keying it this way means a new import can never be rendered
  // against the previous one's response: that response is terminal, so the drawer would skip the
  // progress bar entirely and show the old import's summary and counts until the first poll landed.
  const [tracked, setTracked] = useState({ jobId: null, status: null });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!jobId) return undefined;

    let cancelled = false;
    let delay = POLL_INTERVAL_MS;
    const controller = new AbortController();

    const poll = async () => {
      try {
        const response = await getImportStatus(jobId, controller.signal);
        if (cancelled) return;

        const next = response?.data ?? null;
        setTracked({ jobId, status: next });
        setStoreStatus(next);

        if (next?.isTerminal) return;

        delay = POLL_INTERVAL_MS;
      } catch (error) {
        if (cancelled || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;

        // Keep polling through a transient failure: the import is still running server-side, and
        // giving up here would strand the user on a frozen progress bar. A 429 is the exception —
        // retrying on the usual interval would hold the rate-limit window open indefinitely and
        // take the "download remaining rows" call down with it.
        delay =
          error?.response?.status === 429 ? backoffAfterRateLimit(error, delay) : POLL_INTERVAL_MS;
      }

      if (!cancelled) {
        timerRef.current = setTimeout(poll, delay);
      }
    };

    poll();

    return () => {
      cancelled = true;
      controller.abort();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [jobId, setStoreStatus]);

  // Derived rather than stored: with no job — or with a job we have not heard back about yet —
  // there is nothing to report, and deriving avoids setting state from inside the effect just to
  // clear it.
  const currentStatus = jobId && tracked.jobId === jobId ? tracked.status : null;

  return {
    status: currentStatus,
    isPolling: Boolean(jobId) && !currentStatus?.isTerminal,
  };
}
